<template>
  <div class="change-customer-email-fi">
    <div class="change-customer-email-fi__row">
      <text-input-fi
        ref="emailRef"
        v-model.trim="email"
        required
        name="email"
        class="change-customer-email-fi__input"
        :rules="internalEmailError ? [internalEmailError] : emailRules"
        :maxlength="120"
        :label="fields?.changeEmailInput?.value"
        autocomplete="off"
        data-test="new-email"
        @keyup="validateCharacters(email, 'email')"
        @keydown="enableEmailValidationDefault = false"
        @blur="emailBlurred"
      />
    </div>

    <div class="change-customer-email-fi__row">
      <text-input-fi
        id="password"
        ref="passwordRef"
        v-model="password"
        name="password"
        type="password"
        data-test="new-mail-password"
        required
        :rules="[internalPasswordError || true]"
        :label="dict.get('online-Shop.Global-Labels.Password')"
        autocomplete="new-password"
        @input="validate"
      />
    </div>

    <div class="change-customer-email-fi__hint">
      <icon-fi
        class="change-customer-email-fi__hint-icon"
        icon="info"
      />
      <span class="change-customer-email-fi__hint-text">{{
        fields?.changeEmailInfotext?.value
      }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';

import IconFi from '@/components/atoms/icon-fi/IconFi.vue';
import TextInputFi from '@/components/atoms/text-input-fi/TextInputFi.vue';
import { useDict, useFischer } from '@/composables/fischerPlugin';
import { useValidators } from '@/composables/validators';

defineOptions({ name: 'ChangeCustomerEmailFi' });

const fields = inject<Record<string, any>>('fields');

interface Props {
  passwordError?: string | boolean;
  emailError?: string | boolean;
  newEmail?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  passwordError: false,
  emailError: false,
  newEmail: null,
});

const emit = defineEmits<{
  'form-changed': [payload: { valid: boolean; email: string; password: string }];
}>();

const fischer = useFischer();
const { rules, hasInvalidCharacters, validateCharacters, dynamicRule } = useValidators();

const emailRef = useTemplateRef<InstanceType<typeof TextInputFi>>('emailRef');
const passwordRef = useTemplateRef<InstanceType<typeof TextInputFi>>('passwordRef');

const email = ref('');
const password = ref('');
const enableEmailValidation = ref(false);
const enableEmailValidationDefault = ref(true);
const internalPasswordError = ref<string | boolean>(false);
const internalEmailError = ref<string | boolean>(false);

const emailIdentical = computed(() => {
  const oldEmail = fischer.user.email();
  if (email.value === oldEmail) {
    return [() => fields?.changeEmailNotChanged?.value || 'e-mail not changed'];
  }
  return false;
});

const emailRules = computed(() => {
  if (emailIdentical.value) {
    return emailIdentical.value;
  }
  if (hasInvalidCharacters.value || enableEmailValidation.value) {
    return rules.value.email;
  }
  return dynamicRule(rules.value.email, enableEmailValidationDefault.value);
});

watch(
  () => props.passwordError,
  (newValue) => {
    internalPasswordError.value = newValue;
  },
);

watch(
  () => props.emailError,
  (newValue) => {
    internalEmailError.value = newValue;
  },
);

onMounted(() => {
  if (props.newEmail) {
    email.value = props.newEmail;
  }
});

const validate = () => {
  internalPasswordError.value = false;
  internalEmailError.value = false;
  let isValid = false;

  if (emailRef.value && passwordRef.value) {
    isValid = Boolean(
      email.value &&
      email.value !== '' &&
      !(emailRef.value as any).errorBucket?.length &&
      password.value &&
      password.value !== '' &&
      !(passwordRef.value as any).errorBucket?.length,
    );
  }

  emit('form-changed', {
    valid: isValid,
    email: email.value,
    password: password.value,
  });
};

const emailBlurred = async () => {
  enableEmailValidation.value = true;
  await nextTick();
  validate();
};
const dict = useDict();
</script>

<style src="./change-customer-email-fi" />
